import { MenuItem } from "@blueprintjs/core"
import { ItemPredicate, ItemRendererProps, Select } from "@blueprintjs/select"
import { useState } from "react"
import { useController, UseControllerProps } from "react-hook-form"

import { ToastError } from "../../../components"
import { useDebounce } from "../../../hooks/useDebounce"
import useTradingInstruments from "../hooks/useTradingInstruments"
import { Instrument, InstrumentLight } from "../types"
import { SelectButton } from "./SelectComponent.styled"
import { ErrorIconWithTooltip } from "../../../components"

export const InstrumentSelect = <T extends Record<string, any>>({
  control,
  name,
  disabled,
  placeholder = "Select an instrument",
  rules,
}: UseControllerProps<T> & { placeholder?: string }) => {
  const {
    field: { value, onChange },
    fieldState: { isDirty },
    formState: { errors },
  } = useController({ control, name, rules })

  const [query, setQuery] = useState<string>("")
  const debouncedSetQuery = useDebounce((newQuery: string) => {
    setQuery(newQuery)
  }, 500)

  const { instruments, error } = useTradingInstruments({ query })

  const renderInstrument = (
    item: InstrumentLight,
    { handleClick, modifiers }: ItemRendererProps,
  ) => {
    if (!modifiers.matchesPredicate) return null
    return (
      <MenuItem
        active={modifiers.active}
        key={item.id}
        text={item.bloomberg_code}
        onClick={handleClick}
        roleStructure="listoption"
      />
    )
  }

  const filterInstruments: ItemPredicate<InstrumentLight> = (query, instrument) => {
    if (query.length < 2) return false
    return instrument.bloomberg_code.toLowerCase().includes(query.toLowerCase())
  }

  return (
    <>
      <Select
        items={instruments || []}
        itemRenderer={renderInstrument}
        itemPredicate={filterInstruments}
        onItemSelect={instrument => onChange(instrument)}
        noResults={
          <MenuItem
            disabled
            text={query.length < 2 ? "Start typing to search..." : "No instruments found"}
          />
        }
        onQueryChange={debouncedSetQuery}
        resetOnSelect={true}
        resetOnClose={true}
      >
        <SelectButton
          text={value ? (value as Instrument).bloomberg_code : placeholder}
          rightIcon="caret-down"
          small
          intent={isDirty ? "warning" : "none"}
          disabled={disabled || !!error}
          icon={disabled && "disable"}
        />
      </Select>
      {errors[name] && <ErrorIconWithTooltip errorMessage={String(errors[name]?.message)} />}
      <ToastError
        error={error}
        errorMessage="Unable to get instruments, please try to refresh the page."
      />
    </>
  )
}

export default InstrumentSelect
